import AddressFields from '@/components/address/address-fields'
import { formatPhoneNumber, mask } from '@/lib/form-state/util'
import { OnboardingFormState } from '../onboarding-state'

interface Props {
    value: OnboardingFormState['doctor']
    onChange: (doctor: OnboardingFormState['doctor']) => void
}

/** Dados do médico e o local onde ele atende (vira endereço do profissional). */
export default function DoctorStep({ value, onChange }: Props) {
    const setField = (field: keyof OnboardingFormState['doctor'], fieldValue: string) => {
        onChange({ ...value, [field]: fieldValue })
    }

    return (
        <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-4">
                <h3 className="font-semibold text-base-content">Seus dados</h3>
                <p className="-mt-2 text-sm text-base-content/60">
                    Campos marcados com <span className="text-error">*</span> são
                    obrigatórios.
                </p>

                <div className="grid gap-4 md:grid-cols-2">
                    <div className="form-control">
                        <label className="label pb-1" htmlFor="onboarding-doctor-name">
                            <span className="label-text font-medium">
                                Nome completo
                                <span className="text-error ml-1">*</span>
                            </span>
                        </label>
                        <input
                            id="onboarding-doctor-name"
                            className="input input-bordered w-full"
                            value={value.name}
                            onChange={e => setField('name', e.target.value)}
                            placeholder="Dra. Ana Souza"
                        />
                    </div>

                    <div className="form-control">
                        <label className="label pb-1" htmlFor="onboarding-doctor-phone">
                            <span className="label-text font-medium">
                                Telefone
                                <span className="text-error ml-1">*</span>
                            </span>
                        </label>
                        <input
                            id="onboarding-doctor-phone"
                            className="input input-bordered w-full"
                            value={value.phoneNumber}
                            onChange={e =>
                                setField('phoneNumber', formatPhoneNumber(e.target.value))
                            }
                            placeholder="(11) 99999-9999"
                            inputMode="numeric"
                            maxLength={15}
                        />
                    </div>

                    <div className="form-control">
                        <label className="label pb-1" htmlFor="onboarding-doctor-document">
                            <span className="label-text font-medium">
                                CPF
                                <span className="text-error ml-1">*</span>
                            </span>
                        </label>
                        <input
                            id="onboarding-doctor-document"
                            className="input input-bordered w-full"
                            value={value.document}
                            onChange={e =>
                                setField('document', mask(e.target.value, '999.999.999-99'))
                            }
                            placeholder="000.000.000-00"
                            inputMode="numeric"
                            maxLength={14}
                        />
                        <p className="mt-1 text-xs text-base-content/60 whitespace-normal break-words">
                            Usado no repasse e nos documentos do atendimento.
                        </p>
                    </div>

                    <div className="form-control">
                        <label className="label pb-1" htmlFor="onboarding-doctor-council">
                            <span className="label-text font-medium">
                                Conselho
                                <span className="text-error ml-1">*</span>
                            </span>
                        </label>
                        <input
                            id="onboarding-doctor-council"
                            className="input input-bordered w-full"
                            value={value.councilName}
                            onChange={e => setField('councilName', e.target.value)}
                            placeholder="CRM"
                        />
                    </div>

                    <div className="form-control">
                        <label
                            className="label pb-1"
                            htmlFor="onboarding-doctor-registration"
                        >
                            <span className="label-text font-medium">
                                Número do registro
                                <span className="text-error ml-1">*</span>
                            </span>
                        </label>
                        <input
                            id="onboarding-doctor-registration"
                            className="input input-bordered w-full"
                            value={value.registrationNumber}
                            onChange={e => setField('registrationNumber', e.target.value)}
                            placeholder="123456-SP"
                        />
                    </div>
                </div>
            </section>

            <section className="flex flex-col gap-4">
                <div>
                    <h3 className="font-semibold text-base-content">
                        Local de atendimento
                        <span className="text-error ml-1">*</span>
                    </h3>
                    <p className="text-sm text-base-content/60">
                        É o endereço que aparece na confirmação do agendamento — CEP, rua,
                        número, bairro, cidade e UF são obrigatórios. Você pode cadastrar
                        outros locais depois.
                    </p>
                </div>

                <AddressFields
                    showLabel
                    idPrefix="onboarding-address"
                    value={value.address}
                    onChange={address => onChange({ ...value, address })}
                />
            </section>
        </div>
    )
}
